import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  GraduationCap, ArrowLeft, Calendar, BookOpen, FileText, Award,
  Loader2, CheckCircle2, AlertCircle, Lightbulb, ChevronDown, ChevronUp,
  Target, Trophy, BarChart2, XCircle, AlertTriangle, Clock, ListChecks
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import DescriptiveFeedback from "@/components/DescriptiveFeedback";
import MathText from "@/components/MathText";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

// ── Helpers ──────────────────────────────────────────────────────────────────
const LevelBadge = ({ level }) => {
  const colors = {
    LOW: "bg-red-100 text-red-700 border-red-200",
    MODERATE: "bg-yellow-100 text-yellow-700 border-yellow-200",
    HIGH: "bg-green-100 text-green-700 border-green-200",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${colors[level] || colors.MODERATE}`}>
      {level}
    </span>
  );
};

const fmtDate = (iso) => {
  try { return format(new Date(iso), "dd MMM yyyy, h:mm a"); } catch { return iso || "—"; }
};

const subjectLabel = (s) =>
  s ? s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "—";

// ── Descriptive feedback renderer ─────────────────────────────────────────────
function DescriptiveFeedbackFull({ feedback }) {
  if (!feedback) return null;
  if (typeof feedback === "string") return <p className="text-sm text-gray-600 whitespace-pre-line">{feedback}</p>;
  return (
    <div className="space-y-3">
      {(feedback.clarity || feedback.factual) && (
        <div className="flex gap-3 flex-wrap">
          {feedback.clarity && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5 text-blue-600" />
              <span className="text-xs font-semibold text-gray-700">Clarity</span>
              <LevelBadge level={feedback.clarity} />
            </div>
          )}
          {feedback.factual && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-3 py-2 flex items-center gap-2">
              <BookOpen className="w-3.5 h-3.5 text-purple-600" />
              <span className="text-xs font-semibold text-gray-700">Factual Base</span>
              <LevelBadge level={feedback.factual} />
            </div>
          )}
        </div>
      )}
      {(feedback.good_highlights || []).length > 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-emerald-700 flex items-center gap-1 mb-2">
            <CheckCircle2 className="w-3.5 h-3.5" /> What You Got Right
          </p>
          <div className="space-y-2">
            {feedback.good_highlights.map((h, i) => (
              <div key={i} className="border-l-2 border-emerald-400 pl-2">
                {h.quote && <p className="text-xs italic text-emerald-800 mb-0.5">"{h.quote}"</p>}
                <p className="text-xs text-emerald-700">{h.reason}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {(feedback.improvement_highlights || []).length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-xs font-semibold text-amber-700 flex items-center gap-1 mb-2">
            <AlertTriangle className="w-3.5 h-3.5" /> Improvement Needed
          </p>
          <div className="space-y-2">
            {feedback.improvement_highlights.map((h, i) => (
              <div key={i} className="border-l-2 border-amber-400 pl-2">
                {h.quote && <p className="text-xs italic text-amber-800 mb-0.5">"{h.quote}"</p>}
                <p className="text-xs text-amber-700">{h.suggestion}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      {feedback.missing && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
          <span className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-1">
            <AlertCircle className="w-3.5 h-3.5 text-orange-600" /> What's Missing
          </span>
          <p className="text-xs text-gray-600 leading-relaxed">{feedback.missing}</p>
        </div>
      )}
      {feedback.ideal_answer && feedback.ideal_answer !== "Unable to generate" && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <span className="text-xs font-semibold text-gray-700 flex items-center gap-1 mb-1">
            <Lightbulb className="w-3.5 h-3.5 text-green-600" /> Ideal CBSE Answer
          </span>
          <p className="text-xs text-gray-600 leading-relaxed whitespace-pre-line">
            {(feedback.ideal_answer || "").replace(/\*\*/g, "")}
          </p>
        </div>
      )}
    </div>
  );
}

// ── MCQ Detailed Solutions Panel ──────────────────────────────────────────────
function MCQSolutionsPanel({ results }) {
  if (!results || results.length === 0) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-100 text-center py-4">
        <p className="text-xs text-gray-400 italic">Detailed solutions not available for this attempt.</p>
      </div>
    );
  }

  return (
    <div className="mt-3 pt-3 border-t border-gray-100 space-y-3">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Question-wise Solutions</p>
      {results.map((r, idx) => {
        const isCorrect = r.is_correct;
        const notAttempted = !r.selected || r.selected === "";
        return (
          <div
            key={idx}
            className={`rounded-xl border p-3 text-xs ${
              isCorrect
                ? "bg-green-50 border-green-200"
                : notAttempted
                ? "bg-gray-50 border-gray-200"
                : "bg-red-50 border-red-200"
            }`}
          >
            {/* Q number + status icon */}
            <div className="flex items-center gap-2 mb-2">
              {isCorrect ? (
                <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
              ) : notAttempted ? (
                <AlertTriangle className="w-4 h-4 text-gray-400 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              )}
              <span className="font-bold text-gray-700">Q{idx + 1}</span>
              <span className={`ml-auto text-xs font-semibold ${isCorrect ? "text-green-600" : notAttempted ? "text-gray-400" : "text-red-500"}`}>
                {isCorrect ? "Correct" : notAttempted ? "Not Attempted" : "Wrong"}
              </span>
            </div>

            {/* Question text */}
            <p className="text-gray-800 font-medium mb-3 leading-relaxed">
              <MathText text={r.question} />
            </p>

            {/* Your Selection */}
            <div className={`flex items-start gap-2 rounded-lg px-3 py-2 mb-2 ${
              isCorrect
                ? "bg-green-100 border border-green-300"
                : notAttempted
                ? "bg-gray-100 border border-gray-200"
                : "bg-red-100 border border-red-300"
            }`}>
              <span className="font-bold text-gray-600 mt-0.5 flex-shrink-0">Your Selection:</span>
              <span className={`leading-relaxed ${
                isCorrect ? "text-green-800 font-semibold" : notAttempted ? "text-gray-500 italic" : "text-red-700 font-semibold"
              }`}>
                {notAttempted ? "Not answered" : <MathText text={r.selected} />}
              </span>
            </div>

            {/* Correct Answer (only show separately if wrong or not attempted) */}
            {!isCorrect && (
              <div className="flex items-start gap-2 rounded-lg px-3 py-2 bg-green-100 border border-green-300">
                <span className="font-bold text-gray-600 mt-0.5 flex-shrink-0">Correct Answer:</span>
                <span className="text-green-800 font-semibold leading-relaxed">
                  <MathText text={r.correct} />
                </span>
              </div>
            )}

            {/* Key Info */}
            {r.key_info && (
              <div className="mt-2 flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <Lightbulb className="w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0" />
                <p className="text-blue-700 leading-relaxed"><MathText text={r.key_info} /></p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── MCQ Attempt Card ──────────────────────────────────────────────────────────
function MCQAttemptCard({ attempt }) {
  const [expanded, setExpanded] = useState(false);
  const [solutions, setSolutions] = useState(attempt.results || null);
  const [loading, setLoading] = useState(false);

  const handleExpand = useCallback(async () => {
    if (expanded) { setExpanded(false); return; }
    if (solutions) { setExpanded(true); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/mcq/attempt/${attempt.id}/solutions`);
      setSolutions(res.data?.results || []);
      setExpanded(true);
    } catch {
      toast.error("Could not load solutions");
    }
    setLoading(false);
  }, [attempt.id, expanded, solutions]);

  const pct = attempt.percentage || 0;
  const scoreColor = pct >= 70 ? "text-green-600" : pct >= 45 ? "text-amber-600" : "text-red-600";
  const minutes = attempt.time_taken ? Math.floor(attempt.time_taken / 60) : 0;
  const seconds = attempt.time_taken ? attempt.time_taken % 60 : 0;

  return (
    <Card className="border-gray-200 hover:shadow-md transition-shadow" data-testid={`mcq-attempt-${attempt.id}`}>
      <CardContent className="p-3">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap mb-1">
              <Badge className="bg-blue-100 text-blue-700 border-none text-xs py-0 px-1.5">
                {(attempt.exam_type || "").toUpperCase()}
              </Badge>
              <Badge className="bg-violet-100 text-violet-700 border-none text-xs py-0 px-1.5">
                {subjectLabel(attempt.subject)}
              </Badge>
              {attempt.is_first_attempt === false && (
                <Badge className="bg-orange-100 text-orange-600 border-none text-xs py-0 px-1.5">
                  #{attempt.attempt_number}
                </Badge>
              )}
            </div>
            <p className="text-sm font-semibold text-gray-800 line-clamp-1">
              {attempt.topic ? attempt.topic.replace(/_/g, " ") : "—"}
            </p>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-gray-400">
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {fmtDate(attempt.created_at)}
              </span>
              {attempt.time_taken > 0 && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" /> {minutes}m {seconds}s
                </span>
              )}
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className={`text-xl font-bold ${scoreColor}`}>{attempt.score}<span className="text-xs font-normal text-gray-400">/{attempt.total}</span></p>
            <p className={`text-xs font-semibold ${scoreColor}`}>{pct}%</p>
          </div>
        </div>

        {/* Progress bar */}
        <Progress value={pct} className="h-1.5 mb-2" />

        {/* Expand button */}
        <button
          onClick={handleExpand}
          className="w-full flex items-center justify-center gap-1.5 py-1.5 rounded-lg bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-medium transition-colors"
          data-testid={`mcq-expand-${attempt.id}`}
        >
          {loading ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : expanded ? (
            <><ChevronUp className="w-3.5 h-3.5" /> Hide Solutions</>
          ) : (
            <><ChevronDown className="w-3.5 h-3.5" /> View Detailed Solutions</>
          )}
        </button>

        {/* Solutions */}
        {expanded && <MCQSolutionsPanel results={solutions} />}
      </CardContent>
    </Card>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function History({ user, onLogout }) {
  // Descriptive state
  const [submissions, setSubmissions] = useState([]);
  const [loadingDesc, setLoadingDesc] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);

  // MCQ practice state
  const [mcqAttempts, setMcqAttempts] = useState([]);
  const [loadingMcq, setLoadingMcq] = useState(true);

  // Load both on mount
  useEffect(() => {
    fetchSubmissions();
    fetchMcqAttempts();
  }, []);

  const fetchSubmissions = async () => {
    setLoadingDesc(true);
    try {
      const res = await axios.get(`${API}/submissions/${user?.id || user?.uid}`);
      setSubmissions(res.data || []);
    } catch {
      toast.error("Could not load descriptive history");
    }
    setLoadingDesc(false);
  };

  const fetchMcqAttempts = async () => {
    setLoadingMcq(true);
    try {
      const uid = user?.id || user?.uid;
      const res = await axios.get(`${API}/mcq/my-attempts?user_id=${uid}`);
      setMcqAttempts(res.data?.attempts || []);
    } catch {
      toast.error("Could not load MCQ practice history");
    }
    setLoadingMcq(false);
  };

  // MCQ Stats
  const mcqStats = {
    total: mcqAttempts.length,
    avgPct: mcqAttempts.length ? Math.round(mcqAttempts.reduce((s, a) => s + (a.percentage || 0), 0) / mcqAttempts.length) : 0,
    best: mcqAttempts.length ? Math.max(...mcqAttempts.map((a) => a.percentage || 0)) : 0,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-4 py-4 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <GraduationCap className="w-6 h-6 text-blue-600" />
          <h1 className="text-lg font-bold text-gray-800">My Attempt History</h1>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

          {/* ── LEFT: MCQ Practice ── */}
          <div>
            {/* Section header */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100">
                <ListChecks className="w-4 h-4 text-indigo-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 text-base">MCQ Practice</h2>
                <p className="text-xs text-gray-500">Objective question attempts with solutions</p>
              </div>
            </div>

            {/* Stats strip */}
            {!loadingMcq && mcqAttempts.length > 0 && (
              <div className="grid grid-cols-3 gap-2 mb-4">
                {[
                  { label: "Attempts", value: mcqStats.total, icon: Trophy, color: "text-blue-600", bg: "bg-blue-50" },
                  { label: "Avg Score", value: `${mcqStats.avgPct}%`, icon: BarChart2, color: "text-amber-600", bg: "bg-amber-50" },
                  { label: "Best", value: `${mcqStats.best}%`, icon: Award, color: "text-green-600", bg: "bg-green-50" },
                ].map(({ label, value, icon: Icon, color, bg }) => (
                  <div key={label} className={`${bg} rounded-xl p-2.5 text-center border border-gray-200`}>
                    <Icon className={`w-3.5 h-3.5 ${color} mx-auto mb-0.5`} />
                    <p className={`text-base font-bold ${color}`}>{value}</p>
                    <p className="text-xs text-gray-500">{label}</p>
                  </div>
                ))}
              </div>
            )}

            {/* MCQ list */}
            {loadingMcq ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-indigo-400" /></div>
            ) : mcqAttempts.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <ListChecks className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No MCQ attempts yet</p>
                <p className="text-xs text-gray-400 mt-1">Practice MCQs from your subjects to see history here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {mcqAttempts.map((attempt) => (
                  <MCQAttemptCard key={attempt.id} attempt={attempt} />
                ))}
              </div>
            )}
          </div>

          {/* ── RIGHT: Descriptive Test ── */}
          <div>
            {/* Section header */}
            <div className="flex items-center gap-2 mb-4">
              <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-emerald-100">
                <FileText className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h2 className="font-bold text-gray-800 text-base">Descriptive Test</h2>
                <p className="text-xs text-gray-500">Written answers with AI feedback</p>
              </div>
            </div>

            {/* Descriptive list */}
            {loadingDesc ? (
              <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-emerald-400" /></div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                <FileText className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500 font-medium">No descriptive attempts yet</p>
              </div>
            ) : (
              <div className="space-y-3">
                {submissions.map((sub) => (
                  <Card
                    key={sub.id}
                    className="border-gray-200 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setSelectedSubmission(selectedSubmission?.id === sub.id ? null : sub)}
                    data-testid={`desc-attempt-${sub.id}`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                            <Badge className="bg-emerald-100 text-emerald-700 border-none text-xs py-0 px-1.5">
                              {subjectLabel(sub.subject)}
                            </Badge>
                            {sub.rank && sub.rank <= 3 && (
                              <Badge className="bg-yellow-100 text-yellow-700 border-none text-xs py-0 px-1.5">
                                Rank #{sub.rank}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-700 font-medium line-clamp-2">{sub.question}</p>
                          <p className="text-xs text-gray-400 mt-1">{fmtDate(sub.created_at)}</p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className={`text-xl font-bold ${sub.score >= 3 ? "text-green-600" : sub.score >= 2 ? "text-amber-600" : "text-red-500"}`}>
                            {sub.score}
                          </p>
                          <p className="text-xs text-gray-500">/ {sub.total_marks || 4}</p>
                          {selectedSubmission?.id === sub.id
                            ? <ChevronUp className="w-3.5 h-3.5 text-gray-400 mt-1 ml-auto" />
                            : <ChevronDown className="w-3.5 h-3.5 text-gray-400 mt-1 ml-auto" />
                          }
                        </div>
                      </div>

                      {selectedSubmission?.id === sub.id && (
                        <div className="mt-3 border-t pt-3 space-y-3" onClick={(e) => e.stopPropagation()}>
                          {sub.extracted_text && (
                            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                              <p className="text-xs font-semibold text-gray-500 mb-1">Your Answer (OCR):</p>
                              <p className="text-xs text-gray-700 italic whitespace-pre-wrap">{sub.extracted_text}</p>
                            </div>
                          )}
                          <DescriptiveFeedbackFull feedback={sub.feedback} />
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
